// @spec VUL-STR-008
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { ScannerIntegrationsPage } from '@/features/settings/vulnerabilities/scanner-integrations';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/settings/scanner-integrations')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.scannerIntegrations'),
  }),
  component: ScannerIntegrationsRoute,
});

/**
 * Scanner Integrations settings page route.
 *
 * Allows organization administrators to configure connections to vulnerability
 * scanners like Tenable, Qualys, Rapid7, or custom APIs for importing
 * vulnerability data.
 */
function ScannerIntegrationsRoute() {
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin, isLoading: isLoadingRole } = useOrgRole();

  useEffect(() => {
    if (!isLoadingRole && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isLoadingRole, isAdmin, navigate]);

  if (!isLoadingRole && !isAdmin) {
    return null;
  }

  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  if (isLoadingRole || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <header>
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-5 w-96 mt-2" />
          </header>
          <div className="flex gap-4">
            <Skeleton className="h-10 w-44" />
            <Skeleton className="h-10 w-44" />
            <div className="flex-1" />
            <Skeleton className="h-10 w-36" />
          </div>
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl">
        <ScannerIntegrationsPage />
      </div>
    </div>
  );
}
